import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import {
  LuCheckCircle2, LuChevronRight, LuChevronLeft, LuBuilding2,
} from 'react-icons/lu';

const STEPS = ['Empresa', 'Departamento', 'Funcionário', 'Avaliação', 'Confirmação'];

const getSemaforo = (score) => {
  if (score >= 80) return { cls: 'verde',    label: 'Verde',   color: 'var(--success)', bg: 'var(--success-soft)' };
  if (score >= 60) return { cls: 'amarelo',  label: 'Atenção', color: 'var(--warning)', bg: 'var(--warning-soft)' };
  return              { cls: 'vermelho', label: 'Crítico', color: 'var(--danger)', bg: 'var(--danger-soft)' };
};

const Stepper = ({ step }) => (
  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 32 }}>
    {STEPS.map((label, i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', flex: i < STEPS.length - 1 ? 1 : 0 }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 700,
            background: i < step ? 'var(--accent)' : i === step ? 'var(--surface-3)' : 'var(--surface-2)',
            border: i === step ? '1px solid var(--accent)' : '1px solid var(--border)',
            color: i < step ? 'var(--on-accent)' : i === step ? 'var(--text-1)' : 'var(--text-3)',
          }}>
            {i < step ? '✓' : i + 1}
          </div>
          <span style={{ fontSize: 11, color: i === step ? 'var(--text-1)' : 'var(--text-3)', fontWeight: i === step ? 700 : 400, whiteSpace: 'nowrap' }}>
            {label}
          </span>
        </div>
        {i < STEPS.length - 1 && (
          <div style={{ flex: 1, height: 2, background: i < step ? 'var(--accent)' : 'var(--surface-3)', margin: '0 8px', marginBottom: 20 }} />
        )}
      </div>
    ))}
  </div>
);

// Breadcrumb com seleções feitas
function Breadcrumb({ dept, company }) {
  if (!dept) return null;
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, color: 'var(--text-3)', marginBottom: 16, flexWrap: 'wrap' }}>
      <span style={{ background: 'var(--surface-3)', borderRadius: 6, padding: '2px 10px', color: 'var(--text-2)', fontWeight: 600 }}>{dept.name}</span>
      {company && (
        <>
          <span>›</span>
          <span style={{ background: 'var(--accent-soft)', borderRadius: 6, padding: '2px 10px', color: 'var(--accent)', fontWeight: 600 }}>
            {company.trade_name || company.name}
          </span>
        </>
      )}
    </div>
  );
}

export default function NewEvaluationPage() {
  const [step,            setStep]            = useState(0);
  const [departments,     setDepartments]     = useState([]);
  const [companies,       setCompanies]       = useState([]);
  const [users,           setUsers]           = useState([]);
  const [selectedDept,    setSelectedDept]    = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [configs,         setConfigs]         = useState([]);
  const [operator,        setOperator]        = useState('');
  const [contractor,      setContractor]      = useState('');
  const [refMonth,        setRefMonth]        = useState('');
  const [checks,          setChecks]          = useState({});
  const [observations,    setObservations]    = useState('');
  const [saving,          setSaving]          = useState(false);
  const [saved,           setSaved]           = useState(null);
  const [error,           setError]           = useState('');

  useEffect(() => {
    // Departamentos são buscados só depois que a empresa é escolhida (Passo 0)
    // — ver selectCompany. Isso é necessário para o superadmin, que enxerga
    // departamentos de todas as empresas quando nenhum company_id é informado;
    // para os demais papéis o backend ignora o parâmetro e já devolve só a
    // própria empresa de qualquer forma.
    api.get('/companies', { params: { active: true } }).then((r) => setCompanies(r.data)).catch(console.error);
    api.get('/users').then((r) => setUsers(r.data)).catch(console.error);
  }, []);

  // Usuários filtrados pela empresa selecionada
  const filteredUsers = selectedCompany
    ? users.filter((u) => u.company_id === selectedCompany.id)
    : [];

  const loadConfigs = useCallback(async (deptId) => {
    const r = await api.get(`/departments/${deptId}/indicator-configs`);
    const active = r.data.filter((c) => c.active);
    setConfigs(active);
    const init = {};
    active.forEach((c) => { init[c.slot] = false; });
    setChecks(init);
  }, []);

  const totalScore = configs.reduce((sum, c) => sum + (checks[c.slot] ? c.weight : 0), 0);

  const selectCompany = (company) => {
    setSelectedCompany(company);
    setContractor(company.trade_name || company.name);
    setSelectedDept(null);
    setOperator('');
    setConfigs([]);
    setChecks({});
    setDepartments([]);
    setError('');
    api.get('/departments', { params: { company_id: company.id } })
      .then((r) => setDepartments(r.data))
      .catch(() => setError('Erro ao carregar departamentos da empresa selecionada'));
  };

  const selectDept = async (dept) => {
    setSelectedDept(dept);
    setOperator('');
    setConfigs([]);
    setChecks({});
    setError('');
    try { await loadConfigs(dept.id); } catch { setError('Erro ao carregar indicadores do departamento'); }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        operator,
        contractor,
        reference_month: refMonth,
        department_id: selectedDept.id,
        company_id: selectedCompany.id,
        observations: observations.trim() || null,
      };
      for (let i = 1; i <= 10; i++) {
        payload[`indicator_${i}`] = checks[i] || false;
      }
      const r = await api.post('/assessment', payload);
      setSaved(r.data);
      setStep(4);
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao salvar avaliação');
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setStep(0); setSelectedDept(null); setSelectedCompany(null);
    setConfigs([]); setChecks({});
    setOperator(''); setContractor(''); setRefMonth('');
    setObservations(''); setSaved(null); setError('');
  };

  return (
    <div className="page-card">
      <div className="page-card-header">
        <div>
          <div className="page-card-title">Nova Avaliação</div>
          <div className="page-card-subtitle">Lançamento de avaliação por departamento e operador</div>
        </div>
      </div>

      <Stepper step={step} />
      {error && <div className="alert-custom danger" style={{ marginBottom: 16 }}>{error}</div>}

      {/* ── STEP 0: Empresa ── */}
      {step === 0 && (
        <div>
          <div className="form-section-title">Selecione a Empresa / Filial</div>
          {companies.length === 0 && (
            <p style={{ color: 'var(--text-3)', fontSize: 13 }}>
              Nenhuma empresa cadastrada. Acesse Cadastros → Empresas / Filiais.
            </p>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12, maxWidth: 480 }}>
            {companies.map((c) => (
              <button key={c.id} onClick={() => selectCompany(c)} style={{
                padding: '14px 18px', border: '1px solid',
                borderColor: selectedCompany?.id === c.id ? 'var(--accent)' : 'var(--border)',
                borderRadius: 8,
                background: selectedCompany?.id === c.id ? 'var(--accent-soft)' : 'var(--surface-1)',
                cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', gap: 12,
                transition: 'all 0.15s',
              }}>
                <LuBuilding2 size={18}
                  color={selectedCompany?.id === c.id ? 'var(--accent)' : 'var(--text-3)'} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, color: 'var(--text-1)', fontSize: 14 }}>
                    {c.trade_name || c.name}
                  </div>
                  {c.trade_name && (
                    <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{c.name}</div>
                  )}
                </div>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                  background: c.type === 'matriz' ? 'var(--info-soft)' : 'var(--warning-soft)',
                  color:      c.type === 'matriz' ? 'var(--info)' : 'var(--warning)',
                }}>
                  {c.type === 'matriz' ? 'Matriz' : 'Filial'}
                </span>
              </button>
            ))}
          </div>
          <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn-salvar" disabled={!selectedCompany}
              onClick={() => { setError(''); setStep(1); }}>
              Próximo <LuChevronRight style={{ marginLeft: 6 }} />
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 1: Departamento ── */}
      {step === 1 && (
        <div>
          <div className="form-section-title">Selecione o Departamento</div>
          <Breadcrumb dept={null} company={selectedCompany} />
          {departments.length === 0 && (
            <p style={{ color: 'var(--text-3)', fontSize: 13 }}>Nenhum departamento cadastrado. Acesse Cadastros → Departamentos.</p>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12, maxWidth: 480 }}>
            {departments.map((d) => (
              <button key={d.id} onClick={() => selectDept(d)} style={{
                padding: '14px 18px', border: '1px solid',
                borderColor: selectedDept?.id === d.id ? 'var(--accent)' : 'var(--border)',
                borderRadius: 8,
                background: selectedDept?.id === d.id ? 'var(--accent-soft)' : 'var(--surface-1)',
                cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                transition: 'all 0.15s',
              }}>
                <span style={{ fontWeight: 600, color: 'var(--text-1)', fontSize: 14 }}>{d.name}</span>
                {d.description && <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{d.description}</span>}
              </button>
            ))}
          </div>
          <div style={{ marginTop: 24, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button className="btn-excluir" onClick={() => setStep(0)}>
              <LuChevronLeft style={{ marginRight: 6 }} /> Voltar
            </button>
            <button className="btn-salvar" disabled={!selectedDept}
              onClick={() => { setError(''); setStep(2); }}>
              Próximo <LuChevronRight style={{ marginLeft: 6 }} />
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 2: Funcionário ── */}
      {step === 2 && (
        <div>
          <div className="form-section-title">Dados do Funcionário</div>
          <Breadcrumb company={selectedCompany} dept={selectedDept} />

          {filteredUsers.length === 0 && (
            <div style={{
              padding: '16px 20px', borderRadius: 8, background: 'var(--warning-soft)',
              border: '1px solid var(--warning-border)', fontSize: 13, color: 'var(--warning)', marginBottom: 16,
            }}>
              Nenhum usuário cadastrado para a empresa <strong>{selectedCompany?.trade_name || selectedCompany?.name}</strong>.
              Acesse Sistema → Usuários e vincule funcionários a esta empresa.
            </div>
          )}

          <div className="form-group-row">
            <div className="form-field">
              <label>Funcionário *</label>
              <select className="form-control" value={operator}
                onChange={(e) => setOperator(e.target.value)}
                disabled={filteredUsers.length === 0}>
                <option value="">— selecione —</option>
                {filteredUsers.map((u) => (
                  <option key={u.id} value={u.full_name || u.user}>
                    {u.full_name || u.user}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label>Mês de Referência *</label>
              <input type="month" className="form-control" value={refMonth}
                onChange={(e) => setRefMonth(e.target.value)} />
            </div>
          </div>

          <div style={{ marginTop: 24, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button className="btn-excluir" onClick={() => setStep(1)}>
              <LuChevronLeft style={{ marginRight: 6 }} /> Voltar
            </button>
            <button className="btn-salvar"
              disabled={!operator.trim() || !refMonth}
              onClick={() => { setError(''); setStep(3); }}>
              Próximo <LuChevronRight style={{ marginLeft: 6 }} />
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 3: Avaliação ── */}
      {step === 3 && (
        <div>
          <div className="form-section-title">Indicadores — {selectedDept?.name}</div>
          <Breadcrumb company={selectedCompany} dept={selectedDept} />

          {configs.length === 0 ? (
            <p style={{ color: 'var(--text-3)', fontSize: 13, marginTop: 8 }}>
              Este departamento não possui indicadores ativos. Configure em Cadastros → Indicadores.
            </p>
          ) : (
            <>
              <div className="indicators-grid" style={{ marginTop: 12 }}>
                {configs.map((c) => (
                  <label key={c.slot} className="indicator-check">
                    <input type="checkbox" checked={checks[c.slot] || false}
                      onChange={(e) => setChecks((prev) => ({ ...prev, [c.slot]: e.target.checked }))} />
                    <div className="indicator-check-label">
                      <span className="indicator-check-name">{c.name}</span>
                      <span className="indicator-check-weight">{c.weight} pts</span>
                    </div>
                  </label>
                ))}
              </div>

              {/* Placar ao vivo */}
              <div style={{
                marginTop: 20, padding: '14px 20px', borderRadius: 10,
                background: getSemaforo(totalScore).bg, display: 'flex', alignItems: 'center', gap: 14,
              }}>
                <div style={{
                  width: 48, height: 48, borderRadius: '50%',
                  background: getSemaforo(totalScore).color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--text-1)', fontWeight: 800, fontSize: 14,
                }}>
                  {totalScore}
                </div>
                <div>
                  <div style={{ fontWeight: 700, color: getSemaforo(totalScore).color, fontSize: 15 }}>
                    {getSemaforo(totalScore).label}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-3)' }}>pontuação atual</div>
                </div>
              </div>
            </>
          )}

          <div style={{ marginTop: 20 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-2)', marginBottom: 6 }}>
              Observações
            </label>
            <textarea
              className="form-control"
              rows={3}
              placeholder="Observações adicionais sobre a avaliação (opcional)"
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              style={{ resize: 'vertical', fontSize: 13 }}
            />
          </div>

          <div style={{ marginTop: 24, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button className="btn-excluir" onClick={() => setStep(2)}>
              <LuChevronLeft style={{ marginRight: 6 }} /> Voltar
            </button>
            <button className="btn-salvar" disabled={saving || configs.length === 0}
              onClick={handleSubmit}>
              {saving ? 'Salvando…' : 'Confirmar Avaliação'}
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 4: Confirmação ── */}
      {step === 4 && saved && (() => {
        const finalScore = configs.reduce((sum, c) => sum + (saved[`indicator_${c.slot}`] ? c.weight : 0), 0);
        const sem = getSemaforo(finalScore);
        return (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <LuCheckCircle2 size={56} color="var(--accent)" style={{ marginBottom: 12 }} />
            <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-1)', marginBottom: 4 }}>
              Avaliação registrada com sucesso!
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 4 }}>
              {saved.operator} · {selectedDept?.name} · {saved.reference_month}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 24 }}>
              {selectedCompany?.trade_name || selectedCompany?.name}
            </div>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 12,
              padding: '16px 28px', borderRadius: 12,
              background: sem.bg, marginBottom: 28,
            }}>
              <div style={{
                width: 52, height: 52, borderRadius: '50%', background: sem.color,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--text-1)', fontWeight: 800, fontSize: 18,
              }}>
                {finalScore}
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontWeight: 700, color: sem.color, fontSize: 16 }}>{sem.label}</div>
                <div style={{ fontSize: 12, color: 'var(--text-3)' }}>pontos totais</div>
              </div>
            </div>
            <div>
              <button className="btn-salvar" onClick={reset}>Nova Avaliação</button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
